import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { EnvelopeIcon, PhoneIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { PageTransition } from '../ui';
import { motion } from 'framer-motion';
import { SparklesIcon } from '@heroicons/react/24/solid';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    organizationName: '',
    contactPerson: '',
    email: '',
    phone: '',
    role: 'USER' as 'USER' | 'ADMIN',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Note: We're reusing the 'register' function from auth context for now, 
  // but mapping fields to what the backend likely expects or handling it as a lead.
  // Ideally this should call a 'contact-sales' endpoint.
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // For now, we simulate registration/lead capture.
      // In a real scenario, we might map 'organizationName' to a company field.
      // We'll proceed with basic registration structure for compatibility.
      const registrationData = {
        email: formData.email,
        password: 'TemporaryPassword123!', // Auto-generated for lead
        firstName: formData.contactPerson.split(' ')[0] || 'Sir/Madam',
        lastName: formData.contactPerson.split(' ')[1] || '',
        role: formData.role,
        // Custom fields would go here if backend supports them
      };

      await register(registrationData);
      navigate('/dashboard');
    } catch (err: any) {
      console.error(err);
      // Fallback: If registration fails (e.g. backend validation on password), just show generic error
      // or redirect to a "Thank You" page if we treat this purely as a contact form.
      // For this UI overhaul, we'll assume success redirects.
      setError(err.response?.data?.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition className="min-h-screen flex items-center justify-center bg-neutral-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 bg-white rounded-3xl shadow-2xl overflow-hidden min-h-[600px]">
        {/* Left Side - Form */}
        <div className="flex flex-col justify-center p-8 sm:p-12 lg:p-16 relative z-10 order-2 lg:order-1">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 mb-2">Partner with Us</h1>
            <p className="text-neutral-500">
              Set up your organization and empower your sales team.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <label htmlFor="organizationName" className="text-sm font-medium text-neutral-700 block">Organization Name</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <BuildingOfficeIcon className="h-5 w-5 text-neutral-400 group-focus-within:text-primary-600 transition-colors" />
                </div>
                <input
                  id="organizationName"
                  name="organizationName"
                  type="text"
                  className="block w-full pl-11 pr-4 py-3 bg-neutral-50 border-2 border-transparent focus:bg-white focus:border-primary-500 rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-0 transition-all duration-200"
                  placeholder="Acme Corp"
                  value={formData.organizationName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="contactPerson" className="text-sm font-medium text-neutral-700 block">Contact Person</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-neutral-400 group-focus-within:text-primary-600 transition-colors" />
                </div>
                <input
                  id="contactPerson"
                  name="contactPerson"
                  type="text"
                  className="block w-full pl-11 pr-4 py-3 bg-neutral-50 border-2 border-transparent focus:bg-white focus:border-primary-500 rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-0 transition-all duration-200"
                  placeholder="John Doe"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="email" className="text-sm font-medium text-neutral-700 block">Work Email</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-neutral-400 group-focus-within:text-primary-600 transition-colors" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="block w-full pl-11 pr-4 py-3 bg-neutral-50 border-2 border-transparent focus:bg-white focus:border-primary-500 rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-0 transition-all duration-200"
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="phone" className="text-sm font-medium text-neutral-700 block">Phone Number</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-neutral-400 group-focus-within:text-primary-600 transition-colors" />
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    className="block w-full pl-11 pr-4 py-3 bg-neutral-50 border-2 border-transparent focus:bg-white focus:border-primary-500 rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-0 transition-all duration-200"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-error-50 border border-error-100 p-4 flex items-center gap-3">
                <span className="text-sm font-medium text-error-700">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full relative flex justify-center py-3.5 px-4 border border-transparent text-sm font-semibold rounded-xl text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 hover:-translate-y-0.5 active:translate-y-0"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Submitting...</span>
                </div>
              ) : (
                'Request Access'
              )}
            </button>

            <div className="text-center pt-2">
              <span className="text-sm text-neutral-500">Already have an account? </span>
              <Link to="/login" className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                Sign in
              </Link>
            </div>
          </form>
        </div>

        {/* Right Side - Visual */}
        <div className="hidden lg:block relative bg-neutral-50 overflow-hidden order-1 lg:order-2">
          <div className="absolute inset-0 flex items-center justify-center p-8 bg-gradient-to-br from-blue-50/50 to-purple-50/50">
            {/* 3D Floating Animation */}
            <motion.img
              src={require('../../assets/images/contact-us-illustration.png')}
              alt="Contact Us"
              className="w-full max-w-md object-contain drop-shadow-2xl"
              animate={{
                y: [0, -20, 0],
                rotate: [0, 1, -1, 0]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>

          {/* Overlay Text */}
          <div className="absolute bottom-0 left-0 right-0 p-12 z-10 text-center">
            <h3 className="text-2xl font-bold text-neutral-800 mb-2">Build Your Organization</h3>
            <p className="text-neutral-500">We help you scale your operations efficiently.</p>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default Register;
